// 端末（パスキー）の管理モーダル（docs/AUTH.md の実装順序(7)）。
//
// ここが「掃除できないパスキーの残骸」の出口。利用者が認証器側からパスキーを消しても
// サーバーの行は残るので、**残った行を消す手段**をここで初めて用意する。
//
// 🔴 **削除は取り消せないので、押す前に「どれを消そうとしているか」が分かること**を最優先にする。
//    ⇒ 「この端末」の印（`current`）／2段階の確認／最後の1本のときの警告。
import { t } from '@/platform/i18n';
import { getLanguage } from '@/platform/net/playerId';
import { deleteDevice, fetchDevices, logoutAllDevices, type DeviceRow } from '@/platform/net/account';
import { el, openOverlay } from './modalChrome';

/**
 * 端末一覧を開く。
 * @returns 全端末ログアウトを実行したら true（呼び出し側はこの端末も入り直しが要る）
 */
export function openDevices(): Promise<boolean> {
  return new Promise((resolve) => {
    const { overlay, panel, close } = openOverlay();

    const title = el('div', 'hs-modal-title', t('portal.devices.title'));
    const body = el('div', 'hs-modal-body', t('portal.devices.loading'));
    const err = el('div', 'hs-modal-err', '');
    const row = el('div', 'hs-modal-row');
    const closeBtn = el('button', 'hs-btn hs-btn-ghost', t('portal.devices.close'));
    row.append(closeBtn);
    panel.append(title, body, err, row);

    let loggedOutAll = false;
    const done = () => {
      close();
      resolve(loggedOutAll);
    };
    closeBtn.addEventListener('click', done);
    overlay.addEventListener('mousedown', (e) => {
      if (e.target === overlay) done();
    });

    const render = (devices: DeviceRow[]) => {
      body.replaceChildren();
      if (devices.length === 0) {
        // 引き継ぎコードで入った人はここに来る（セッションはあるがパスキーは無い）。
        body.append(el('div', '', t('portal.devices.empty')));
      } else {
        const list = el('div', 'hs-devices');
        for (const d of devices) list.append(deviceRow(d, devices.length, render, err));
        body.append(list);
      }
      body.append(el('div', 'hs-field-note', t('portal.devices.note')));

      // 「すべての端末からログアウト」＝ 紛失時の操作。ここに置くのは、
      // 端末一覧を見て「知らない端末がある」と気づいた人が次に取る行動だから。
      const allBtn = el('button', 'hs-btn hs-btn-ghost hs-devices-all', t('portal.devices.logoutAll'));
      let confirming = false;
      allBtn.addEventListener('click', () => {
        if (!confirming) {
          confirming = true;
          allBtn.textContent = t('portal.devices.logoutAllConfirm');
          allBtn.classList.add('danger');
          return;
        }
        (allBtn as HTMLButtonElement).disabled = true;
        allBtn.textContent = t('portal.devices.working');
        void logoutAllDevices().then((ok) => {
          if (!ok) {
            err.textContent = t('portal.devices.error');
            (allBtn as HTMLButtonElement).disabled = false;
            allBtn.textContent = t('portal.devices.logoutAll');
            allBtn.classList.remove('danger');
            confirming = false;
            return;
          }
          loggedOutAll = true;
          done();
        });
      });
      body.append(allBtn);
    };

    void fetchDevices().then((devices) => {
      if (!devices) {
        // 401 ＝ セッションが無い／切れている。**失敗ではなく状態**なので、次の一手を出す。
        body.textContent = t('portal.devices.needLogin');
        return;
      }
      render(devices);
    });
  });
}

/** 1行ぶん。削除は2段階（押す → 確認）で、確認の文言は状況によって変える。 */
function deviceRow(
  d: DeviceRow,
  total: number,
  rerender: (devices: DeviceRow[]) => void,
  err: HTMLElement,
): HTMLElement {
  const wrap = el('div', `hs-device${d.current ? ' current' : ''}`);
  const head = el('div', 'hs-device-head');
  head.append(el('span', 'hs-device-name', d.label ?? t('portal.devices.unnamed')));
  if (d.current) head.append(el('span', 'hs-device-badge', t('portal.devices.current')));
  wrap.append(head);
  wrap.append(
    el(
      'div',
      'hs-device-meta',
      t('portal.devices.dates', { created: fmtDate(d.createdAt), used: fmtDate(d.lastUsedAt) }),
    ),
  );

  const actions = el('div', 'hs-device-actions');
  const delBtn = el('button', 'hs-linkbtn danger', t('portal.devices.remove'));
  let confirming = false;
  delBtn.addEventListener('click', () => {
    if (!confirming) {
      confirming = true;
      // 🔴 状況ごとに警告を変える。「最後の1本」と「今使っている端末」は取り返しが違う。
      const key =
        total === 1
          ? 'portal.devices.removeLast'
          : d.current
            ? 'portal.devices.removeCurrent'
            : 'portal.devices.removeConfirm';
      wrap.append(el('div', 'hs-device-warn', t(key)));
      delBtn.textContent = t('portal.devices.removeYes');
      return;
    }
    (delBtn as HTMLButtonElement).disabled = true;
    delBtn.textContent = t('portal.devices.working');
    err.textContent = '';
    void deleteDevice(d.id).then(async (ok) => {
      if (!ok) {
        err.textContent = t('portal.devices.error');
        (delBtn as HTMLButtonElement).disabled = false;
        delBtn.textContent = t('portal.devices.remove');
        return;
      }
      // 消えた結果をサーバーから取り直す（手元で行を抜くと、失敗したときに画面と食い違う）。
      const next = await fetchDevices();
      rerender(next ?? []);
    });
  });
  actions.append(delBtn);
  wrap.append(actions);
  return wrap;
}

function fmtDate(ms: number): string {
  if (!ms) return '—';
  try {
    return new Date(ms).toLocaleDateString(getLanguage());
  } catch {
    return '—';
  }
}
